import { formatDate, useDebounce } from "../../helpers";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { globalSearch } from "../../services/globalSearch";
import "./index.scss";
import constants from "../../constants";

interface ICircuit {
  label: string;
  value: string;
}
interface IPlayer {
  playerId: number;
  givenName: string;
  familyName: string;
  profileImageId: number;
  playerNationalityCode: string;
  playerNationality: string;
  playerProfileLink: string;
  profileImage: string;
  playedCircuits: ICircuit[];
  FullName: string;
  wtnRating: number | null;
  proPlayer?: boolean;
}

interface ITournament {
  id: number;
  name: string;
  promotionalName: string | null;
  hostNation: string | null;
  hostNationCode: string | null;
  venue: string | null;
  startDate: string;
  endDate: string;
  tournamentKey: string;
  tennisCategoryCode: string;
  tournamentLink: string;
  hospitality: null | string;
  year: number;
  liveStreamingUrl: null | string;
}

interface IResource {
  id: number;
  name: string;
  url: string;
  extension: string;
}

interface ISearchResults {
  players: IPlayer[];
  tournaments: ITournament[];
  resources: IResource[];
}

const Player = ({
  profileImage,
  playerProfileLink,
  playerId,
  wtnRating,
  playerNationalityCode,
  proPlayer,
  FullName
}: IPlayer): JSX.Element => {
  return (
    <div className="main-nav__search--block">
      <a
        className="main-nav__search--thumb"
        style={{
          backgroundImage: `url("${profileImage}")`
        }}
        href={playerProfileLink}
        target="_blank"
      />
      <div className="main-nav__search--details">
        <a href={playerProfileLink} target="_blank">
          {FullName}
          {!constants.omitNationality.includes(playerNationalityCode)
            ? ` (${playerNationalityCode})`
            : null}
          {wtnRating ? (
            <span className="WTN_rating">
              <span>{proPlayer ? "ProZone" : wtnRating.toFixed(1)}</span>
            </span>
          ) : null}
        </a>
      </div>
      <div className="main-nav__search--buttons">
        <a
          href={playerProfileLink}
          target="_blank"
          className="btn btn--primary"
        >
          Profile
        </a>
        <a
          href={`/en/news-and-media/articles/?playerId=${playerId}&player=${FullName.toLowerCase().replace(
            /\s/g,
            "-"
          )}`}
          target="_blank"
          className="btn btn--primary"
        >
          News
        </a>
      </div>
    </div>
  );
};

const Tournament = ({
  name,
  tournamentKey,
  tournamentLink,
  startDate,
  endDate,
  tennisCategoryCode
}: ITournament): JSX.Element => {
  const regexTournament = /(\bwimbledon\b)|(\baustralian open\b)|(\broland garros\b)|(\bus open\b)|(\bolympic\b)|(\bbillie\b)|(\bdavis\b)/g;
  const isLogoVisible =
    !regexTournament.test(name.toLocaleLowerCase()) &&
    !tournamentLink.startsWith("http");
  const isOlympic =
    tournamentKey.startsWith("M-OL") ||
    tournamentKey.startsWith("W-OL") ||
    tournamentKey.startsWith("WC-POL");

  return (
    <div className="main-nav__search--block">
      <div className="main-nav__search--details">
        <a href={tournamentLink} target="_blank">
          {name}
          <span>
            ({formatDate(startDate).shortNoYear} -{" "}
            {formatDate(endDate).shortNoYear} {new Date(endDate).getFullYear()})
          </span>
        </a>
      </div>
      {isLogoVisible || isOlympic ? (
        <div className="main-nav__search-right">
          <div
            className={`main-nav__search--logo ${tennisCategoryCode} ${
              isOlympic ? "olympic-logo" : ""
            }`}
          />
        </div>
      ) : null}
    </div>
  );
};

const Resource = ({ name, url }: IResource): JSX.Element => {
  return (
    <div className="main-nav__search--block">
      <div className="main-nav__search--details">
        <a href={url} target="_blank">
          {name}
        </a>
      </div>
    </div>
  );
};

const RenderItem = {
  players: Player,
  tournaments: Tournament,
  resources: Resource
};

const GlobalSearch = () => {
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isVisibleSearchPane, setIsVisibleSearchPane] = useState<boolean>(
    false
  );
  const [timeout, updateTimeout] = useState<number>(0);
  const [searchResults, setSearchResults] = useState<ISearchResults | null>(
    null
  );

  const blurSearchInput = useCallback(() => {
    if (searchValue.length) {
      console.log(`globalSearch: ${searchValue}`);
      const payload = {
        event: "globalSearch",
        searchTerm: searchValue
      };
      //@ts-ignore
      dataLayer.push(payload);
    }
  }, [searchValue]);

  const inputSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const body: HTMLElement | null = document.body;
    const head: HTMLElement | null = document.querySelector("head");
    const matchWidget: HTMLElement | null = document.querySelector(
      "header .match-ribbon-widget"
    );
    const searchStyleNode: HTMLElement | null = document.querySelector(
      "#search-style-node"
    );
    const widgetIndentTop = 20;
    if (
      matchWidget !== null &&
      matchWidget.clientHeight > widgetIndentTop &&
      head !== null &&
      searchStyleNode === null
    ) {
      let style = document.createElement("style");
      style.id = "search-style-node";
      style.innerHTML = `@media (min-width: 1024px) {.main-nav__search--body {top: ${matchWidget.clientHeight}px}}`;
      head.appendChild(style);
    }
    if (body !== null) body.style.position = isVisibleSearchPane ? "fixed" : "";
    inputSearchRef.current && inputSearchRef.current.focus();
  }, [isVisibleSearchPane]);

  useEffect(() => {
    if (!searchValue.length) {
      setSearchResults(null);
      return;
    }
    (async () => {
      setIsSearchLoading(true);
      try {
        const data: ISearchResults = await globalSearch(searchValue);
        setSearchResults(data);
      } finally {
        setIsSearchLoading(false);
      }
    })();
  }, [searchValue]);

  return (
    <div className="main-nav__search">
      <a
        title="Search"
        className="search search--global"
        onClick={() => setIsVisibleSearchPane(true)}
        href="#"
      />
      <div
        className={`main-nav__search--body ${
          isVisibleSearchPane ? "visible" : ""
        }`}
      >
        <div className={`container ${isSearchLoading ? " page-loading" : ""}`}>
          <div className="main-nav__search--input">
            <input
              type="text"
              ref={inputSearchRef}
              placeholder="Search players, tournaments and resources"
              onChange={e =>
                useDebounce({
                  delay: 300,
                  func: (value: string) => {
                    setSearchValue(value);
                  },
                  timeout,
                  updateTimeout,
                  params: [e.target.value]
                })
              }
              onBlur={() => blurSearchInput()}
            />
            <a
              onClick={() => {
                setIsVisibleSearchPane(false);
                setSearchValue("");
              }}
              className="main-nav__search--input-close"
            ></a>
          </div>
          {isSearchLoading ? null : (
            <div className="main-nav__search--list">
              {searchResults &&
                Object.keys(searchResults).map((key: string) =>
                  searchResults[key].length > 0 ? (
                    <div key={key}>
                      <h3>{key}</h3>
                      {searchResults[key].map(
                        (
                          data: ITournament | IPlayer | IResource,
                          index: number
                        ) => {
                          const Component = RenderItem[key];
                          return (
                            <Component {...data} key={`${key}-${index}`} />
                          );
                        }
                      )}
                    </div>
                  ) : null
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
